import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Alert,
  Divider
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useScriptBuilder } from '../../hooks/useScriptBuilder';
import { useItemAutoSave } from '../../hooks/useItemAutoSave';
import { BuilderToolbar } from '../BuilderToolbar';
import { StepList } from '../StepList';
import { TestPointsSection } from '../TestPointsSection';
import type { Script } from '../../types';
import type { ItemRef } from '../../types/steps';
import type { SaveStatus } from '../../hooks/useItemAutoSave';

type EditorMode = 'visual' | 'json';

type Props = {
  masterScript: Script;
  children: Script[];
  childrenLoading?: boolean;
  childrenLoaded?: boolean;
  onAddItems: () => void;
  onDuplicateItem: (item: Script) => void;
  onRemoveItem: (id: string) => void;
  onRemoveItems?: (ids: string[]) => void;
};

export const BuilderTab = ({
  masterScript,
  children: items,
  childrenLoading,
  childrenLoaded = false,
  onAddItems,
  onDuplicateItem,
  onRemoveItem,
  onRemoveItems
}: Props) => {
  const queryClient = useQueryClient();
  const builder = useScriptBuilder(masterScript.scriptData);

  const [editorMode, setEditorMode] = useState<EditorMode>(
    builder.parseError ? 'json' : 'visual'
  );
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const jsonBaselineRef = useRef('');
  const jsonTextRef = useRef('');
  jsonTextRef.current = jsonText;

  const markClean = useCallback(() => {
    builder.markClean();
    jsonBaselineRef.current = jsonTextRef.current;
  }, [builder]);

  const masterGetScriptDataRef = useRef(() => builder.toJson());
  masterGetScriptDataRef.current = () =>
    editorMode === 'json' ? jsonText : builder.toJson();
  const masterGetScriptData = useCallback(
    () => masterGetScriptDataRef.current(),
    []
  );

  const { saveStatus: masterSaveStatus, saveNow: masterSaveNow } =
    useItemAutoSave({
      scriptId: masterScript.id,
      isDirty:
        builder.isDirty ||
        (editorMode === 'json' && jsonText !== jsonBaselineRef.current),
      getScriptData: masterGetScriptData,
      markClean,
      activeScript: masterScript
    });

  const masterSaveNowRef = useRef(masterSaveNow);
  masterSaveNowRef.current = masterSaveNow;

  const [itemSaveStatuses, setItemSaveStatuses] = useState<SaveStatus[]>([]);

  useEffect(() => {
    // Reconcile the master's items[] against its child scripts — but ADDITIVELY.
    // Guard 1: only act on a settled, successful children query. While loading —
    // or on error, where react-query leaves `items` as its empty default — bail.
    if (childrenLoading || !childrenLoaded) return;

    // Guard 2: NEVER prune references off an empty children list. `/children`
    // (GetMqttScriptChildrenQuery) derives from the master's own cells[].items[]
    // and excludes orphans, so during a migration-window mismatch it can return
    // an empty 200 while the master still legitimately has items. Treating that
    // as "every child was deleted" is exactly what wiped the test points in the
    // 2026-07 incident (the destructive prune below then auto-saved items: []).
    // Only trust the list to REMOVE a reference when it is non-empty; always
    // allow appends. (Cost: deleting the very last item leaves one stale ref
    // until reload — acceptable vs. silently destroying a full test-point list.)
    const canPrune = items.length > 0;

    const childIds = new Set(items.map(i => i.id));
    const assignedIds = new Set(builder.items.map(i => i.scriptId));

    const filtered = canPrune
      ? builder.items.filter(i => childIds.has(i.scriptId))
      : builder.items;
    const unassigned = items.filter(i => !assignedIds.has(i.id));

    const changed =
      filtered.length !== builder.items.length || unassigned.length > 0;

    if (changed) {
      const newEntries: ItemRef[] = unassigned.map(child => ({
        globalId: child.globalId,
        scriptId: child.id
      }));
      const newOrderedList = [...filtered, ...newEntries];

      queryClient.cancelQueries({ queryKey: ['scripts', masterScript.id] });
      builder.setItems(newOrderedList);
      setTimeout(() => masterSaveNowRef.current(), 0);
    }
  }, [items, childrenLoading, childrenLoaded]);

  useEffect(() => {
    if (builder.parseError) {
      setEditorMode('json');
      setJsonText(
        masterScript.scriptData ??
          '{\n  "pickupSteps": [],\n  "items": [],\n  "returnSteps": []\n}'
      );
    }
  }, [builder.parseError, masterScript.scriptData]);

  const handleModeChange = useCallback(
    (newMode: EditorMode) => {
      if (newMode === 'json') {
        const baseline = masterScript.scriptData ?? builder.toJson();
        jsonBaselineRef.current = baseline;
        setJsonText(baseline);
        setJsonError(null);
        setEditorMode('json');
        return;
      }
      try {
        const parsed = JSON.parse(jsonText);
        if (!parsed || typeof parsed !== 'object')
          throw new Error('Invalid script data structure');
        builder.loadScriptData(jsonText);
        setJsonError(null);
        setEditorMode('visual');
      } catch (e) {
        setJsonError(e instanceof Error ? e.message : 'Invalid JSON');
      }
    },
    [builder, jsonText]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <BuilderToolbar
        editorMode={editorMode}
        onModeChange={handleModeChange}
        saveStatuses={[masterSaveStatus, ...itemSaveStatuses]}
      />

      {builder.parseError && (
        <Alert severity="warning">
          Script data could not be parsed: {builder.parseError}
        </Alert>
      )}

      {jsonError && (
        <Alert severity="error" onClose={() => setJsonError(null)}>
          Cannot switch to Visual mode: {jsonError}
        </Alert>
      )}

      {editorMode === 'visual' ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Divider textAlign="left" sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Pickup Steps
            </Typography>
          </Divider>
          <StepList
            steps={builder.pickupSteps}
            onAddStep={type => builder.addStep(type, 'pickupSteps')}
            onRemoveStep={id => builder.removeStep(id, 'pickupSteps')}
            onUpdateStep={(id, updates) =>
              builder.updateStep(id, updates, 'pickupSteps')
            }
            onReorder={(activeId, overId) =>
              builder.reorderSteps(activeId, overId, 'pickupSteps')
            }
          />

          <Divider sx={{ my: 2 }} />
          <TestPointsSection
            items={builder.items}
            childScripts={items}
            parentScriptId={masterScript.id}
            onAddItems={onAddItems}
            onDuplicateItem={onDuplicateItem}
            onRemoveItem={onRemoveItem}
            onRemoveItems={onRemoveItems}
            onReorder={builder.reorderItems}
            onItemSaveStatusesChange={setItemSaveStatuses}
          />

          <Divider textAlign="left" sx={{ my: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Return Steps
            </Typography>
          </Divider>
          <StepList
            steps={builder.returnSteps}
            onAddStep={type => builder.addStep(type, 'returnSteps')}
            onRemoveStep={id => builder.removeStep(id, 'returnSteps')}
            onUpdateStep={(id, updates) =>
              builder.updateStep(id, updates, 'returnSteps')
            }
            onReorder={(activeId, overId) =>
              builder.reorderSteps(activeId, overId, 'returnSteps')
            }
          />
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Master Script Data (JSON)
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            JSON mode shows master script data only (pickup steps + items +
            return steps). Item scripts are edited individually in Visual mode.
          </Alert>
          <TextField
            multiline
            fullWidth
            minRows={12}
            maxRows={30}
            value={jsonText}
            onChange={e => setJsonText(e.target.value)}
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.85rem' }
            }}
          />
        </Paper>
      )}
    </Box>
  );
};
